import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import { routes } from '../config';
import { fetchMemberCoupons } from '../api/auth';
import type { Coupon } from '../types';
import { useLocalization } from '../contexts/LocalizationContext';
import { useStoreSettings } from '../contexts/StoreSettingsContext';

export default function CouponDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocalization();
  const { storeSettings } = useStoreSettings();
  const locationState = location.state as { selectedIndex?: number; coupons?: Coupon[]; couponId?: string } | null;
  const [activeIndex, setActiveIndex] = useState(locationState?.selectedIndex || 0);
  const [coupons, setCoupons] = useState<Coupon[]>(locationState?.coupons || []);
  const [qrCodes, setQrCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(!locationState?.coupons);

  useEffect(() => {
    if (coupons.length > 0) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchMemberCoupons();
        const list = Array.isArray(data) ? data : [];
        setCoupons(list);
        if (locationState?.couponId) {
          const idx = list.findIndex((c) => c.couponId === locationState.couponId);
          if (idx >= 0) setActiveIndex(idx);
        }
      } catch {
        setCoupons([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (coupons.length === 0) return;
    Promise.all(
      coupons.map(c => QRCode.toDataURL(c.code, { width: 336, margin: 2, color: { dark: '#000', light: '#fff' } }))
    ).then(setQrCodes);
  }, [coupons]);

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (loading) {
    return (
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#3a3a3a' }}>
        <Typography sx={{ color: '#fff', fontSize: '14px' }}>{t('couponDetail.loading')}</Typography>
      </Box>
    );
  }

  if (coupons.length === 0) {
    return (
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#3a3a3a' }}>
        <Typography sx={{ color: '#fff', fontSize: '14px' }}>{t('couponDetail.noCoupons')}</Typography>
      </Box>
    );
  }

  const coupon = coupons[activeIndex];
  const isExpired = coupon.status === 'EXPIRED'
    || (coupon.usageLimitPerMember > 0 && coupon.redemptionsCount >= coupon.usageLimitPerMember);
  const statusColor = isExpired
    ? (storeSettings?.colorPalettes?.warning || '#A0A0A0')
    : '#7BA88A';
  const statusText = isExpired ? t('couponDetail.expired') : t('couponDetail.active');

  return (
    /* Scrollable container — dark bg fills everything behind */
    <Box sx={{
      position: 'absolute', inset: 0,
      overflowY: 'auto', overflowX: 'hidden',
      backgroundColor: '#3a3a3a',
      '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
    }}>

      {/* Sticky nav bar */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: '48px', px: 2,
        backgroundColor: '#3a3a3a',
      }}>
        <Box
          component="button"
          onClick={() => navigate(routes.coupons)}
          aria-label="Back"
          sx={{
            width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '28px', fontWeight: 300, lineHeight: 1, fontFamily: 'inherit',
            borderRadius: '10px',
            '&:active': { background: 'rgba(255,255,255,0.1)' },
          }}
        >‹</Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#fff' }}>
          {activeIndex + 1} of {coupons.length}
        </Typography>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'rgba(123,168,138,0.55)', width: 40, textAlign: 'right' }}>
          Coupon
        </Typography>
      </Box>

      {/* Coupon card — centered, natural height */}
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pb: '24px', position: 'relative' }}>
        {activeIndex > 0 && (
          <Box component="button" onClick={() => setActiveIndex(i => i - 1)} sx={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
            width: 32, height: 32, border: 'none', background: 'rgba(255,255,255,0.15)',
            borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: '20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit',
            '&:active': { background: 'rgba(255,255,255,0.25)' },
          }}>‹</Box>
        )}

        {/* Ticket card */}
        <Box sx={{ width: 'min(310px, 92%)', backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.28)', overflow: 'visible' }}>
          {/* Upper: QR + instruction */}
          <Box sx={{ px: '16px', pt: '20px', pb: '14px', textAlign: 'center' }}>
            {qrCodes[activeIndex] ? (
              <Box component="img" src={qrCodes[activeIndex]} alt="QR Code"
                sx={{ width: 'clamp(160px, 60vw, 248px)', height: 'clamp(160px, 60vw, 248px)', display: 'block', mx: 'auto', mb: '10px' }} />
            ) : (
              <Box sx={{ width: 'clamp(160px, 60vw, 248px)', height: 'clamp(160px, 60vw, 248px)', mx: 'auto', mb: '10px', backgroundColor: '#f5f5f5', borderRadius: '8px' }} />
            )}
            <Typography sx={{ fontSize: '12px', color: '#9a9a9a', lineHeight: 1.35 }}>
              {t('couponDetail.qrInstruction')}
            </Typography>
          </Box>

          {/* Perforation divider */}
          <Box sx={{ position: 'relative', height: '20px' }}>
            <Box sx={{ position: 'absolute', top: '50%', left: '-9px', transform: 'translateY(-50%)', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#3a3a3a', zIndex: 2 }} />
            <Box sx={{ position: 'absolute', top: '50%', right: '-9px', transform: 'translateY(-50%)', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#3a3a3a', zIndex: 2 }} />
            <Box sx={{ position: 'absolute', top: '50%', left: '14px', right: '14px', transform: 'translateY(-50%)', borderTop: '1.5px dashed #e0e0e0' }} />
          </Box>

          {/* Lower: status + title */}
          <Box sx={{ px: '20px', pt: '8px', pb: '20px', textAlign: 'left' }}>
            <Typography sx={{ fontSize: '12px', color: statusColor, mb: '4px' }}>{statusText}</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#111', lineHeight: 1.25, wordBreak: 'break-word' }}>
              {coupon.name}
            </Typography>
          </Box>
        </Box>

        {activeIndex < coupons.length - 1 && (
          <Box component="button" onClick={() => setActiveIndex(i => i + 1)} sx={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
            width: 32, height: 32, border: 'none', background: 'rgba(255,255,255,0.15)',
            borderRadius: '50%', cursor: 'pointer', color: '#fff', fontSize: '20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit',
            '&:active': { background: 'rgba(255,255,255,0.25)' },
          }}>›</Box>
        )}
      </Box>

      {/* White details section */}
      <Box sx={{ backgroundColor: '#fff', borderRadius: '22px 22px 0 0', px: 2, pt: '20px', pb: '40px', minHeight: '40%' }}>
        <Box sx={{ mb: '18px' }}>
          <Typography sx={{ fontSize: '12px', color: '#B0B0B0', mb: '6px' }}>
            {t('couponDetail.detailsTitle')}
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>
            {coupon.name}
          </Typography>
          {coupon.description && (
            <Typography sx={{ fontSize: '14px', color: '#6a6a6a', mt: '4px', lineHeight: 1.5 }}>
              {coupon.description}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: '18px' }}>
          <Typography sx={{ fontSize: '12px', color: '#B0B0B0', mb: '6px' }}>
            {t('couponDetail.expireDate')}
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#6a6a6a' }}>
            {coupon.expiresAt ? formatDate(coupon.expiresAt) : t('couponDetail.noExpiry')}
          </Typography>
        </Box>

        {(coupon.importantNotice || coupon.importantNoticeZh || coupon.importantNoticeCn) && (
          <Box>
            <Typography sx={{ fontSize: '12px', color: '#B0B0B0', mb: '6px' }}>
              {t('couponDetail.termsConditions')}
            </Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#6a6a6a', lineHeight: 1.5 }}>
              {coupon.importantNotice || coupon.importantNoticeZh || coupon.importantNoticeCn}
            </Typography>
          </Box>
        )}
      </Box>

    </Box>
  );
}
